import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Indicator,
  Loader,
  Popover,
  Text,
  Title,
} from '@mantine/core';
import {
  BellSimpleIcon,
  BellSimpleSlashIcon,
  GearIcon,
} from '@phosphor-icons/react';

import NotificationSplitView from '@/common/notifications/NotificationSplitView.tsx';
import { useNavigate } from '@tanstack/react-router';
import { useNotificationCoordinator } from '@/common/notifications/NotificationCoordinator.tsx';
import type { NotificationDto } from '@/generated/api/pacApi.schemas.ts';
import {
  type SseEvent,
  type SseStatus,
  useSseState,
} from '@/common/notifications/cross-tab-sse-store.ts';
import {
  getBrowserNotificationPermission,
  requestBrowserNotificationPermission,
} from '@/common/notifications/browser-notifications.ts';
import {
  getNotificationsControllerGetNotificationsQueryKey,
  useNotificationsControllerMarkAllNotificationsAsRead,
} from '@/generated/api/notifications.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useProductAnalytics } from '@/common/analytics/product-analytics.tsx';
import { PRODUCT_ANALYTICS_EVENT } from '@/common/analytics/events.ts';

type NavbarNotificationButtonProps = {
  notifications: NotificationDto[];
  totalCount: number;
  unreadCount: number;
  status?: SseStatus;
  error?: string | null;
  title?: string;
  emptyLabel?: string;
  iconSize?: number;
};

const iconSize = 20;

export default function NavbarNotificationButton({
  notifications,
  totalCount,
  unreadCount,
  status,
  error,
}: NavbarNotificationButtonProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const capture = useProductAnalytics();

  const [opened, setOpened] = useState(false);

  const [markedReadThisTime, setMarkedReadThisTime] = useState<string[]>([]);

  const unreadNotifications = useMemo(
    () =>
      notifications.filter(
        (n) => n.readAt === null || markedReadThisTime.includes(n.recipientId),
      ),
    [notifications, markedReadThisTime],
  );

  const closePopover = () => {
    setMarkedReadThisTime([]);
    setOpened(false);
  };

  const markAllAsReadMutation =
    useNotificationsControllerMarkAllNotificationsAsRead({
      mutation: {
        onSuccess: () => {
          capture(PRODUCT_ANALYTICS_EVENT.notificationsMarkedRead, {
            scope: 'all',
            count_bucket: getCountBucket(unreadCount),
          });
        },
        onSettled: () => {
          void queryClient.invalidateQueries({
            queryKey: getNotificationsControllerGetNotificationsQueryKey(),
          });
        },
      },
    });

  const browserNotificationPermission = getBrowserNotificationPermission();
  const notificationsUnsupported = browserNotificationPermission === null;
  const notificationsDisabled = browserNotificationPermission !== 'granted';

  const hasIssues = !notificationsUnsupported && notificationsDisabled;

  const indicatorUnreadCount = unreadCount
    ? unreadCount
    : hasIssues
      ? '!'
      : unreadCount;
  const indicatorColor = unreadCount
    ? undefined
    : hasIssues
      ? 'yellow'
      : undefined;

  return (
    <Popover
      opened={opened}
      onChange={(value) => (value ? setOpened(true) : closePopover())}
      position="bottom-end"
      width={620}
      shadow="md"
      withinPortal
    >
      <Popover.Target>
        <Indicator
          label={indicatorUnreadCount}
          color={indicatorColor}
          offset={{ x: 10, y: 2 }}
          size={14}
        >
          <ActionIcon
            variant="subtle"
            aria-label={
              unreadCount > 0
                ? `Open notifications, ${unreadCount} unread`
                : 'Open notifications'
            }
            onClick={() =>
              setOpened((value) => {
                if (!value) {
                  capture(PRODUCT_ANALYTICS_EVENT.notificationCenterViewed, {
                    unread_count_bucket: getCountBucket(unreadCount),
                    realtime_status: status ?? 'unknown',
                  });
                }
                return !value;
              })
            }
            me={10}
          >
            {error ? (
              <BellSimpleSlashIcon size={iconSize} color="red" />
            ) : status === 'connecting' ? (
              <Loader size={iconSize} />
            ) : (
              <BellSimpleIcon
                color={unreadCount ? undefined : 'gray'}
                size={iconSize}
                weight={unreadCount > 0 ? 'fill' : 'regular'}
              />
            )}
          </ActionIcon>
        </Indicator>
      </Popover.Target>

      <Popover.Dropdown p={0} w={680}>
        {hasIssues ? (
          <Alert color="yellow" variant="light" p="xs">
            <Group justify="space-between">
              Browser notifications are disabled
              <Group gap={0}>
                <Button
                  size="compact-xs"
                  color="yellow"
                  variant="transparent"
                  onClick={() => requestBrowserNotificationPermission()}
                >
                  Enable Notifications
                </Button>
              </Group>
            </Group>
          </Alert>
        ) : null}
        <Group justify="space-between" px="md" py="sm" wrap="nowrap">
          <Box>
            <Title order={5}>Notifications</Title>

            {notifications.length > 0 && (
              <Text size="xs" c="dimmed">
                {totalCount} total
              </Text>
            )}
          </Box>

          <Group>
            <Button
              variant="transparent"
              size="compact-xs"
              disabled={unreadCount <= 0}
              onClick={() => markAllAsReadMutation.mutate()}
            >
              Mark All Read
            </Button>
            {status === 'connected' || status === 'listening' ? (
              <Badge variant="light" color={unreadCount > 0 ? 'blue' : 'gray'}>
                {unreadCount > 0 ? `${unreadCount} unread` : 'All read'}
              </Badge>
            ) : (
              <Badge variant="light" color="red">
                Reconnecting
              </Badge>
            )}
            <ActionIcon
              variant="transparent"
              onClick={() => navigate({ to: '/me/notifications/preferences' })}
            >
              <GearIcon />
            </ActionIcon>
          </Group>
        </Group>

        {error && (
          <Box px="md" pb="sm">
            <Text size="xs" c="red" lineClamp={2}>
              {error}
            </Text>
          </Box>
        )}

        <Divider />

        <NotificationSplitView
          notifications={unreadNotifications}
          emptyLabel="No unread notifications."
          onMarkedRead={(id) => setMarkedReadThisTime((prev) => [...prev, id])}
        />

        {/*<Divider />*/}

        {/*<Group*/}
        {/*  justify="end"*/}
        {/*  wrap="nowrap"*/}
        {/*  style={{ backgroundColor: 'var(--mantine-color-gray-1)' }}*/}
        {/*>*/}
        {/*  <Button onClick={() => {}} variant="transparent">*/}
        {/*    Log Out*/}
        {/*  </Button>*/}
        {/*</Group>*/}
      </Popover.Dropdown>
    </Popover>
  );
}

function getCountBucket(count: number) {
  if (count === 0) return '0';
  if (count === 1) return '1';
  if (count <= 5) return '2_5';
  if (count <= 20) return '6_20';
  return '21_plus';
}

export function NotificationButton() {
  const sseState = useSseState();

  return (
    <ConnectedNavbarNotificationButton
      events={sseState.messages}
      status={sseState.state}
      tabIsLeader={sseState.isLeader}
      error={null}
    />
  );
}

function ConnectedNavbarNotificationButton({
  events,
  status,
  tabIsLeader,
  error,
  title,
  emptyLabel,
  iconSize,
}: {
  events: SseEvent[];
  status: SseStatus;
  tabIsLeader: boolean;
  error: string | null;
  title?: string;
  emptyLabel?: string;
  iconSize?: number;
}) {
  const notificationState = useNotificationCoordinator({
    events,
    status,
    tabIsLeader,
  });

  return (
    <NavbarNotificationButton
      notifications={notificationState.notifications}
      totalCount={notificationState.totalCount}
      unreadCount={notificationState.unreadCount}
      status={status}
      error={error}
      title={title}
      emptyLabel={emptyLabel}
      iconSize={iconSize}
    />
  );
}
